import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

const MIME_TYPE = 'audio/webm';

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Push-to-talk dictation 
 * Records a short clip with MediaRecorder; on stop,
 * uploads it to `/v1/transcribe` and hands the returned text to `onText` (called
 * once with the full transcript). Strictly additive — typing always works, and any
 * failure only surfaces a toast.
 *
 * @param {object} apiClient - provides `transcribeAudio(dataUrl)`.
 * @param {(text: string) => void} onText - called once with the transcribed text.
 * @param {object} [options]
 * @param {string} [options.deviceId] - microphone to record from. Empty or
 *   'default' means let the OS choose.
 * @returns {{ isRecording: boolean, isTranscribing: boolean, toggle: () => void, stop: () => void }}
 */
export function useDictation(apiClient, onText, { deviceId } = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  // The live mic stream, exposed so the UI can visualize the audio level.
  const [mediaStream, setMediaStream] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async () => {
    if (recorderRef.current) {
      return;
    }
    let stream;
    const constraints =
      deviceId && deviceId !== 'default'
        ? { audio: { deviceId: { exact: deviceId } } }
        : { audio: true };
    try {
      stream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      toast.error('Microphone access is needed to dictate.');
      return;
    }
    streamRef.current = stream;
    setMediaStream(stream);

    const recorder = new MediaRecorder(stream, { mimeType: MIME_TYPE });
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };
    recorder.onstop = async () => {
      releaseStream();
      const blob = new Blob(chunksRef.current, { type: MIME_TYPE });
      chunksRef.current = [];
      recorderRef.current = null;
      // Always report back (even on empty/failure) so the consumer can leave its
      // processing state; empty string signals "nothing usable".
      const report = (text) => onTextRef.current && onTextRef.current(text);
      if (blob.size === 0) {
        setIsTranscribing(false);
        report('');
        return;
      }
      setIsTranscribing(true);
      try {
        const dataUrl = await blobToDataUrl(blob);
        const rsp = await apiClient.transcribeAudio(dataUrl);
        if (rsp && rsp.error) {
          // Backend returned 2xx/201 with an {error} body (e.g. bad audio format).
          report('');
        } else {
          report(((rsp && rsp.text) || '').trim());
        }
      } catch (err) {
        report('');
      } finally {
        setIsTranscribing(false);
      }
    };

    recorderRef.current = recorder;
    recorder.start();
    setIsRecording(true);
  }, [apiClient, releaseStream, deviceId]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop(); // fires onstop -> transcription
    } else {
      releaseStream();
      recorderRef.current = null;
    }
    setMediaStream(null);
    setIsRecording(false);
  }, [releaseStream]);

  // Stop recording and discard the audio — no transcription. Used when the user
  // switches to typing or closes the modal mid-recording.
  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null;
      recorder.stop();
    }
    recorderRef.current = null;
    chunksRef.current = [];
    releaseStream();
    setMediaStream(null);
    setIsRecording(false);
    setIsTranscribing(false);
  }, [releaseStream]);

  const toggle = useCallback(() => {
    if (recorderRef.current) {
      stop();
    } else {
      start();
    }
  }, [start, stop]);

  // Stop + release the mic on unmount without firing a late transcription.
  useEffect(() => {
    // just cleanup
    return () => {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = null;
        recorder.stop();
        recorderRef.current = null;
      }
      releaseStream();
    };
  }, [releaseStream]);

  return { isRecording, isTranscribing, mediaStream, toggle, stop, cancel };
}

export default useDictation;
